import { useState } from 'react';
import { selectVendorUser, VendorUserType } from 'auth/VendorAuth/vendorUserSlice';
import axios, { AxiosError } from 'axios';
import { ElementLibraryFilter } from 'pages/ElementLibrary';
import { useQuery, UseQueryOptions } from 'react-query';
import { AppConfigState } from 'store/appConfigSlice';
import { showApiErrorMessage, showErrorMessage } from 'store/errorModalSlice';
import { initialFilterValues } from '../components/RfiList/RfiListSidebar';
import { initialTemplateFilterValues } from '../components/TemplateList/TemplateListSidebar';
import { useAppDispatch, useAppSelector } from './redux';

/** get app configuration from server */
export const useAppConfig = (
  options?: Omit<UseQueryOptions<AppConfigState>, "queryKey" | "queryFn">
) => {
  const [isServerDown, setIsServerDown] = useState(false);
  const { rfiId } = useAppSelector(selectVendorUser);
  const dispatch = useAppDispatch();

  const serverDownOptions = isServerDown
    ? {
        refetchOnWindowFocus: true,
        staleTime: 0,
      }
    : {
        refetchOnWindowFocus: false,
        staleTime: Infinity, // fetch once per page load
      };

  return useQuery<AppConfigState>(
    ["appConfig", rfiId],
    async () => {
      const response = await axios.get("/api/v1/appconfig", {
        params: { rfiId: rfiId },
      });
      return response.data;
    },
    {
      retry: 2,
      onSuccess: () => {
        if (isServerDown) setIsServerDown(false);
        // console.log("No Error");
      },
      onError: (error) => {
        console.log("Error Occurred", error);
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 500) {
            if (!isServerDown) setIsServerDown(true);
          }
          if (error.response?.status === 404) {
            dispatch(
              showErrorMessage(
                `Error getting all RFI data from server - ${error.message}`
              )
            );
          }
        }
      },
      ...serverDownOptions,
      ...options,
    }
  );
};

export const useGetRfiVendor = (id: any) => {
  const dispatch = useAppDispatch();

  let results = useQuery(["getRfiVendor", id], () => {
    if (id === null) return null;

    return axios
      .get(`/api/v1/Rfi/${id}`)
      .then((res) => res.data)
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 404) {
            return null; // return null here instead of erroring so the query doesn't retry
          }
        }
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error getting all RFI data from server - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
      });
  });
  return results;
};

interface dataFilter {
  field: string;
  operator: string;
  value: string;
}

const addDay = (dateStr: any) => {
  const date = new Date(dateStr);
  const next_date = new Date(date.setDate(date.getDate() + 1));
  const newDate = new Date(next_date).toISOString().split("T")[0];
  return newDate;
};

export const rfiFilterTransform = (filter: typeof initialFilterValues) => {
  let filters = [];
  if (filter) {
    if (filter.rfiNumber)
      filters.push({
        field: "rfiNumber",
        operator: "contains",
        value: filter.rfiNumber,
      });
    if (filter.title)
      filters.push({
        field: "title",
        operator: "contains",
        value: filter.title,
      });
    if (
      filter.category.length !== undefined &&
      typeof filter.category.length !== "undefined"
    ) {
      if (filter.category.length > 0) {
        let result = filter.category.map(({ value }) => value);
        filters.push({
          field: "categoryId",
          operator: "in",
          value: JSON.stringify(result),
        });
      }
    }
    //TODO: Add one day to toDates
    if (filter.createDateFrom)
      filters.push({
        field: "dateCreated",
        operator: "gte",
        value: filter.createDateFrom,
      });
    if (filter.createDateTo)
      filters.push({
        field: "dateCreated",
        operator: "lt",
        value: addDay(filter.createDateTo),
      });
    if (filter.postDateFrom)
      filters.push({
        field: "postedToSamDate",
        operator: "gte",
        value: filter.postDateFrom,
      });
    if (filter.postDateTo)
      filters.push({
        field: "postedToSamDate",
        operator: "lt",
        value: addDay(filter.postDateTo),
      });
    if (filter.responsesDueDateFrom)
      filters.push({
        field: "responsesDueDate",
        operator: "gte",
        value: filter.responsesDueDateFrom,
      });
    if (filter.responsesDueDateTo)
      filters.push({
        field: "responsesDueDate",
        operator: "lt",
        value: addDay(filter.responsesDueDateTo),
      });
    if (filter.qaCloseDateFrom)
      filters.push({
        field: "qaCloseDate",
        operator: "gte",
        value: filter.qaCloseDateFrom,
      });
    if (filter.qaCloseDateTo)
      filters.push({
        field: "qaCloseDate",
        operator: "lt",
        value: addDay(filter.qaCloseDateTo),
      });
    if (filter.status.length > 0) {
      let result = filter.status.map(({ value }) => value);
      filters.push({
        field: "status",
        operator: "in",
        value: JSON.stringify(result),
      });
    }
    if (filter.createdById.length > 0) {
      let result = filter.createdById.map(({ value }) => value);
      filters.push({
        field: "createdById",
        operator: "in",
        value: JSON.stringify(result),
      });
    }
    if (filter.ownedById.length > 0) {
      let result = filter.ownedById.map(({ value }) => value);
      filters.push({
        field: "ownedById",
        operator: "in",
        value: JSON.stringify(result),
      });
    }
  }
  //console.log("filters", filters);
  return filters;
};

export const templateFilterTransform = (
  filter: typeof initialTemplateFilterValues
) => {
  let filters = [];
  if (filter) {
    if (filter.name)
      filters.push({ field: "name", operator: "contains", value: filter.name });
    if (
      filter.category.length !== undefined &&
      typeof filter.category.length !== "undefined"
    ) {
      if (filter.category.length > 0) {
        let result = filter.category.map(({ value }) => value);
        filters.push({
          field: "categoryId",
          operator: "in",
          value: JSON.stringify(result),
        });

        if (filter.lastUpdatedById.length > 0) {
          let result = filter.lastUpdatedById.map(({ value }) => value);
          filters.push({
            field: "lastEditedByName",
            operator: "in",
            value: JSON.stringify(result),
          });
        }
      }
    }
    //TODO: Add one day to toDates
    if (filter.lastEditedDateFrom)
      filters.push({
        field: "dateLastEdited",
        operator: "gte",
        value: filter.lastEditedDateFrom,
      });
    if (filter.lastEditedDateTo)
      filters.push({
        field: "dateLastEdited",
        operator: "lt",
        value: addDay(filter.lastEditedDateTo),
      });

    if (filter.lastUpdatedById.length > 0) {
      let result = filter.lastUpdatedById.map(({ value }) => value);
      filters.push({
        field: "lastUpdatedUserId",
        operator: "in",
        value: JSON.stringify(result),
      });
    }
  }
  //console.log("filters", filters);
  return filters;
};

export const useRfiData = (
  skip: number,
  take: number,
  sort: any,
  filter: any,
  options: any
) => {
  const dispatch = useAppDispatch();

  let results = useQuery(
    ["getRfiData", skip, take, sort, filter, options],
    () => {
      return axios
        .post(`/api/v1/rfi/GetRfiData`, {
          skip: skip,
          take: take,
          sort: sort,
          filter: rfiFilterTransform(filter),
          options: options,
        })
        .then((res) => res.data)
        .catch((error) => {
          typeof error.response?.data?.[0]?.error?.message === "undefined"
            ? dispatch(
                showErrorMessage(
                  `Error getting all RFI data from server - ${error.message}`
                )
              )
            : dispatch(showApiErrorMessage({ errorResponse: error }));
        });
    }
  );
  //console.log(results);
  return results;
};

export const useTemplateData = (
  skip: number,
  take: number,
  sort: any,
  filter: any,
  options: any
) => {
  const dispatch = useAppDispatch();

  let results = useQuery(
    ["getTemplateData", skip, take, sort, filter, options],
    () => {
      return axios
        .post(`/api/v1/rfitemplates/GetTemplateData`, {
          skip: skip,
          take: take,
          sort: sort,
          filter: templateFilterTransform(filter),
          options: options,
        })
        .then((res) => res.data)
        .catch((error) => {
          typeof error.response?.data?.[0]?.error?.message === "undefined"
            ? dispatch(
                showErrorMessage(
                  `Error getting all RFI templates from server - ${error.message}`
                )
              )
            : dispatch(showApiErrorMessage({ errorResponse: error }));
        });
    }
  );
  //console.log(results);
  return results;
};

export const useAllRfiData = () => {
  const dispatch = useAppDispatch();

  let results = useQuery(["getAllRfis"], () => {
    return axios
      .get(`/api/v1/PublicRfi/GetAllRfi`)
      .then((res) => res.data)
      .catch((error) => {
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error getting all RFI data from server - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
      });
  });
  //console.log(results);
  return results;
};

export const useRfi = (id: any, includeElements: boolean) => {
  const dispatch = useAppDispatch();

  return useQuery<any>(
    ["rfiData", id, includeElements],
    async () => {
      return axios
        .get(`/api/v1/Rfi/id/${id}`, {
          params: { includeElements: includeElements },
        })
        .then((res) => res.data)
        .catch((error: Error | AxiosError) => {
          console.log(error);
          dispatch(
            showErrorMessage(`There was an error copying element - ${error}`)
          );
        });
    },
    {
      cacheTime: 0,
    }
  );
};

// export const publishRfiToSAM = async (RfiId:number) => {

//   const dispatch = useAppDispatch();

//   let results = await axios
//       .put(`/api/v1/rfi${RfiId}/publish`)
//       .then((res) => res.data)
//       .catch((error) => {
//         alert(error)
//         typeof error.response?.data?.[0]?.error?.message === "undefined"
//           ? dispatch(
//               showErrorMessage(
//                 ` hi Error getting all RFI data from server - ${error.message}`
//               )
//             )
//           : dispatch(showApiErrorMessage({ errorResponse: error }));
//       });

//   console.log(results);

//   return results;
// }

export const useRfiElements = (id: number, section: string) => {
  const dispatch = useAppDispatch();

  return useQuery<any>(["rfiElements", id, section], async () => {
    if (!id || !section) {
      return [];
    }

    return axios
      .get(`/api/v1/rfi/${id}/elements/${section}`)
      .then((res) => res.data)
      .catch((error: Error | AxiosError) => {
        console.log(error);
        dispatch(
          showErrorMessage(`There was an error copying element - ${error}`)
        );
      });
  });
};

export const useAttachmentWhiteList = () => {
  const dispatch = useAppDispatch();

  let results = useQuery(["getAttachmentWhiteList"], () => {
    return axios
      .get(`/api/v1/Rfi/GetAttachmentExtensionWhitelist`)
      .then((res) => res.data)
      .catch((error) => {
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error getting history data from server - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
      });
  });
  //console.log(results);
  return results;
};

export const useGetCategories = () => {
  const dispatch = useAppDispatch();

  const { data, isLoading } = useQuery(["getCategories"], () => {
    return axios
      .get(`/api/v1/PublicRfi/GetCategories`)
      .then((res) => res.data)
      .catch((error) => {
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error getting all RFI data from server - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
      });
  });
  return { data, isLoading };
};

export const useGetCategoryReport = () => {
  const dispatch = useAppDispatch();

  const { data, isLoading } = useQuery(["getCategoryReport"], () => {
    return axios
      .get(`/api/v1/rfi/GetRfiCategoryReport`)
      .then((res) => res.data)
      .catch((error) => {
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error getting RFI category report from server - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
      });
  });
  return { data, isLoading };
};

const libraryElementFilterTransform = (filter: ElementLibraryFilter) => {
  let filters = [];

  if (filter) {
    if (filter.elementType && filter.elementType.length > 0) {
      let result = filter.elementType?.map(({ value }) => value);
      filters.push({
        field: "elementType",
        operator: "in",
        value: JSON.stringify(result),
      });
    }
    if (filter.fieldLabel)
      filters.push({
        field: "fieldLabel",
        operator: "contains",
        value: filter.fieldLabel,
      });
    if (filter.fieldType && filter.fieldType.length > 0) {
      let result = filter.fieldType?.map(({ value }) => value);
      filters.push({
        field: "fieldType",
        operator: "in",
        value: JSON.stringify(result),
      });
    }
    if (filter.categories && filter.categories.length > 0) {
      let result = filter.categories.map(({ label }) => label);
      filters.push({
        field: "categories",
        operator: "category",
        value: JSON.stringify(result),
      });
    }
    if (filter.dateLastEditedFrom)
      filters.push({
        field: "dateLastEdited",
        operator: "gte",
        value: filter.dateLastEditedFrom,
      });
    if (filter.dateLastEditedTo)
      filters.push({
        field: "dateLastEdited",
        operator: "lte",
        value: filter.dateLastEditedTo,
      });
    if (filter.lastUpdatedUserName && filter.lastUpdatedUserName.length > 0) {
      let result = filter.lastUpdatedUserName.map(({ value }) => value);
      filters.push({
        field: "lastUpdatedUserId",
        operator: "in",
        value: JSON.stringify(result),
      });
    }
  }
  return filters;
};

export const useGetLibraryElements = (
  skip: number,
  take: number,
  sort: any,
  filter: ElementLibraryFilter,
  options: any
) => {
  const dispatch = useAppDispatch();

  let results = useQuery(
    ["getLibraryElements", skip, take, sort, filter, options],
    () => {
      return axios
        .post(`/api/v1/elements/library/filter`, {
          skip: skip,
          take: take,
          sort: sort,
          filter: libraryElementFilterTransform(filter),
          options: options,
        })
        .then((res) => res.data)
        .catch((error) => {
          typeof error.response?.data?.[0]?.error?.message === "undefined"
            ? dispatch(
                showErrorMessage(
                  `Error getting Library data from server - ${error.message}`
                )
              )
            : dispatch(showApiErrorMessage({ errorResponse: error }));
        });
    }
  );
  //console.log(results);
  return results;
};

export const useUpdateQuestionStatus = (curStatus: any, customerId: any) => {
  const dispatch = useAppDispatch();
  const [options, setOptions] = useState({
    currentStatus: curStatus,
    libraryElementId: customerId,
  });

  const { data, refetch } = useQuery(["useUpdateStatus", options], () => {
    return axios
      .post(`/api/PublicRfi/UpdateStatus`, { params: options })
      .then((res) => res.data)
      .catch((error) => {
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error getting all RFI data from server - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
      });
  });
  return { data, refetch };
};

export const useWeatherTable = () => {
  const dispatch = useAppDispatch();

  let results = useQuery(["getWeatherTableData"], () => {
    return axios
      .get(`/api/v1/WeatherForecast`)
      .then((res) => res.data)
      .catch((error) => {
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error getting history data from server - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
        // navigate("/home");
      });
  });
  return results;
};

export const useVendorUserProfile = (
  options: Omit<
    UseQueryOptions<VendorUserType, unknown, VendorUserType, "getUserProfile">,
    "queryKey" | "queryFn"
  > = {}
) => {
  return useQuery(
    "getUserProfile",
    async () => {
      const res = await axios.get<VendorUserType>(
        `/api/v1/VendorUsers/profile`
      );
      //console.log("getUserProfile", res);
      return res.data;
    },
    options
  );
};

export const useGetVendorQuestions = (rfiId: number, answerStatus: string) => {
  const dispatch = useAppDispatch();

  let results = useQuery(["getVendorQuestions"], () => {
    return axios
      .get(`/api/v1/rfi/${rfiId}/VendorQuestions`, {
        params: { answerStatus: answerStatus },
      })
      .then((res) => res.data)
      .catch((error) => {
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error getting all RFI data from server, - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
      });
  });

  return results;
};

export const useGetCustomerUser = (customerUserId: any) => {
  const dispatch = useAppDispatch();

  let results = useQuery(["getCustomerUser"], () => {
    return axios
      .get(`/api/v1/account/getCustomerUser`, {
        params: { customerUserId: customerUserId },
      })
      .then((res) => res.data)
      .catch((error) => {
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error getting customer user data from server - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
      });
  });

  return results;
};
/*
  export const useVendorLogout  = () => {
    const dispatch = useAppDispatch();
    const { logOut }: IAuthContext = useContext(AuthContext)
    const [isError, setIsError] = useState(false);

    const vendorLogOut = useCallback((): void  => {
      const initialState: VendorUserType = {
        sub: "",
        email: "",        
      };
*/

export const useGetRoles = () => {
  const dispatch = useAppDispatch();

  let results = useQuery(["getRoles"], async () => {
    return await axios.get(`/api/v1/Account/GetRoles`).then((res) => res.data);
  });
  //console.log(results);
  return results;
};
